import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useBooth } from "../../contexts/BoothContext";
import logo from "../../images/logo.png";

export default function WalkUpBookingSection() {
	const navigate = useNavigate();
	const { booth, isBoothAvailable } = useBooth();

	const [isStarting, setIsStarting] = useState(false);

	const handleGetStarted = async () => {
		try {
			setIsStarting(true);

			// Create a temporary session for the walk-up flow
			// This should follow the same pattern as reservation flow but without time selection
			const sessionData = {
				boothId: booth.boothId,
				boothObjectId: booth._id,
				boothType: booth.boothType,
				boxPlacement: booth.boxPlacement,
				locationId: booth.location._id || booth.location,
				lockId: booth.lockId,
				saltoLocationId: booth.location?.saltoLocationId,
				boothData: booth,
				// For walk-up, we don't have specific time slots - it's immediate
				time: "Now",
				duration: "Open-ended",
				endTime: "Until checkout",
				date: 0, // Today
				flowType: "walkup",
			};

			// Navigate to a walk-up specific check-in page that mirrors StartSession
			navigate(`/booth/${booth._id}/checkin`, {
				state: sessionData,
			});
		} catch (error) {
			console.error("Error starting walk-up session:", error);
		} finally {
			setIsStarting(false);
		}
	};

	if (!isBoothAvailable) {
		const isOccupied = booth?.isOccupied;
		const status = booth?.status;

		return (
			<div className="bg-white min-h-screen flex flex-col justify-center mx-smPagePadding md:mx-mdPagePadding lg:mx-lgPagePadding xl:mx-xlPagePadding">
				{/* Pebble Logo */}
				<div className="flex flex-col justify-center items-center mb-6">
					<img
						src={logo}
						alt="PebbleSpaces Logo"
						className="w-40 h-auto object-contain"
					/>
				</div>

				{/* Booth Unavailable Message */}
				<div
					className={`border px-6 py-8 rounded-lg text-center ${
						isOccupied
							? "bg-red-50 border-red-200 text-red-800"
							: "bg-yellow-50 border-yellow-200 text-yellow-800"
					}`}
				>
					<div className="flex items-center justify-center mb-4">
						{isOccupied ? (
							<svg
								className="w-12 h-12 text-red-500"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
								/>
							</svg>
						) : (
							<svg
								className="w-12 h-12 text-yellow-500"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
								/>
							</svg>
						)}
					</div>

					<h2 className="text-2xl font-bold mb-2">
						{isOccupied ? "Booth Currently Occupied" : "Booth Unavailable"}
					</h2>

					<p className="text-lg mb-4">
						{isOccupied
							? "Someone is currently using this booth. Please try another booth or wait until it becomes available."
							: `This booth is currently ${
									status || "unavailable"
							  }. Please try another booth or contact support.`}
					</p>

					{isOccupied && (
						<div className="mt-6 p-4 bg-white rounded-md border border-red-200">
							<h3 className="font-semibold text-red-800 mb-2">
								What you can do:
							</h3>
							<ul className="text-sm space-y-1 text-left">
								<li>• Look for another available booth nearby</li>
								<li>• Wait for the current session to end</li>
								<li>• Contact support if you need assistance</li>
							</ul>
						</div>
					)}
				</div>

				{/* Still show pricing info for reference */}
				<div className="mt-8 opacity-60">
					<div className="mb-6">
						<h2 className="text-xl font-bold text-black mb-3 text-center">
							When Available - Pricing
						</h2>
						<ul className="text-sm text-gray-600 space-y-1 list-disc ml-5">
							<li>$1 to unlock a pod</li>
							<li>${booth.costPerMinute} per minute while inside</li>
							<li>Only pay for what you use</li>
						</ul>
					</div>
				</div>

				{/* Footer Message */}
				<div className="text-center mt-8">
					<p className="text-sm text-gray-600 leading-relaxed">
						Need help? Contact support at{" "}
						<span className="font-semibold underline">
							team@pebblespaces.com
						</span>
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="bg-white min-h-screen flex flex-col justify-center mx-smPagePadding md:mx-mdPagePadding lg:mx-lgPagePadding xl:mx-xlPagePadding">
			{/* Pebble Logo */}
			<div className="flex flex-col justify-center items-center mb-6">
				<img
					src={logo}
					alt="PebbleSpaces Logo"
					className="w-40 h-auto object-contain"
				/>
			</div>

			{/* Tagline */}
			<div className="text-center mb-8">
				<p className="text-xl text-black font-normal tracking-tight">
					Your space to focus, meet, or reset.
				</p>
			</div>

			{/* Pricing Section */}
			<div className="mb-8">
				<h2 className="text-2xl font-bold text-black mb-4 text-center">
					Pricing
				</h2>
				<ul className="text-sm text-gray-700 space-y-1 list-disc ml-5">
					<li>$1 to unlock a pod</li>
					<li>${booth.costPerMinute} per minute while inside</li>
					<li>Only pay for what you use</li>
					<li>
						30 minutes ≈ ${Math.round(booth.costPerMinute * 30).toFixed(2)} · 60
						minutes ≈ ${Math.round(booth.costPerMinute * 60).toFixed(2)}
					</li>
					<li>Charged automatically when you leave</li>
				</ul>
			</div>

			{/* How it Works Section */}
			<div className="mb-12">
				<h2 className="text-2xl font-bold text-black mb-4 text-center">
					How It Works
				</h2>
				<ul className="text-sm text-gray-700 space-y-1 list-disc ml-5">
					<li>Walk-up only, first come first served</li>
					<li>Session starts right after unlocking</li>
					<li>
						Automatic checkout when you exit and close the door. Infrared
						sensors detect when you've left
					</li>
					<li>Live timer and cost display during your session</li>
				</ul>
			</div>

			{/* Get Started Button */}
			<div className="flex justify-center mb-8">
				<button
					onClick={handleGetStarted}
					disabled={isStarting}
					className="bg-black text-white px-8 w-full whitespace-nowrap py-4 rounded-lg text-base font-semibold hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
				>
					{isStarting ? "Starting..." : "Get Started"}
				</button>
			</div>

			{/* Footer Message */}
			<div className="text-center">
				<p className="text-sm text-gray-700 leading-relaxed">
					Pebbles are a shared amenity.
					<br />
					Please leave the space better than you found it!
				</p>
			</div>
		</div>
	);
}
